import { useCallback, useEffect, useRef, useState } from 'react'
import { apiRequest } from '../../../../api/client'
import type { TimelineTimeEditRequestDict, TimelineTimeEditStatusDict } from '../../../../generated/nodeDataTypes'
import type { JsonValue } from '../../../../lib/gotrueTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import type { TimelineClip } from './types'
import { editableCurve, type SpeedPoint } from './speedCurve'

const log = createNadDebug('dw:timeline-overhaul:time')
export function decodeStatus(data: JsonValue): TimelineTimeEditStatusDict {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) throw new Error('Invalid time edit response')
  const status = data.status
  if (typeof data.job_id !== 'string' || (status !== 'pending' && status !== 'running' && status !== 'success' && status !== 'failed' && status !== 'cancelled')
    || (data.source_path !== null && typeof data.source_path !== 'string')
    || (data.duration !== null && (typeof data.duration !== 'number' || !Number.isFinite(data.duration) || data.duration <= 0))
    || (data.error !== null && typeof data.error !== 'string')) throw new Error('Invalid time edit response fields')
  return { job_id: data.job_id, status, source_path: data.source_path, duration: data.duration, error: data.error }
}

export function useTimelineTimeEdit(projectId: string | undefined, getClip: (id: string) => TimelineClip | undefined,
  onApply: (id: string, path: string, duration: number, mode: 'reverse' | 'freeze' | 'speed_curve', speedCurve?: TimelineClip['speedCurve']) => void) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const controller = useRef<AbortController | null>(null)
  useEffect(() => () => controller.current?.abort(), [])
  const run = useCallback(async (clip: TimelineClip, mode: 'reverse' | 'freeze' | 'speed_curve', playhead: number, points?: SpeedPoint[]) => {
    if (!projectId || controller.current) return
    const request = new AbortController()
    controller.current = request
    setBusy(true)
    setError(null)
    const body: TimelineTimeEditRequestDict = { source_path: clip.sourcePath, mode,
      start_time: clip.trimStart, end_time: clip.originalDuration - clip.trimEnd,
      freeze_at: clip.trimStart + (playhead - clip.startOffset) * clip.speed }
    if (mode === 'speed_curve') {
      const source = editableCurve(clip)
      body.source_path = source.sourcePath
      body.start_time = source.start
      body.end_time = source.end
      body.points = points
    }
    try {
      let result = decodeStatus(await apiRequest<JsonValue>(`/projects/${projectId}/timeline-time`, {
        method: 'POST', body: JSON.stringify(body), signal: request.signal,
      }))
      log({ event: 'time-edit-submission-received', project_id: projectId, clip_id: clip.id, job_id: result.job_id, mode })
      const expires = Date.now() + 15 * 60 * 1000
      while (result.status === 'pending' || result.status === 'running') {
        if (Date.now() > expires) throw new Error('Time edit is still processing. Try again later.')
        await new Promise<void>((resolve, reject) => {
          const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')) }
          const timer = setTimeout(() => { request.signal.removeEventListener('abort', abort); resolve() }, 1500)
          request.signal.addEventListener('abort', abort, { once: true })
          if (request.signal.aborted) abort()
        })
        result = decodeStatus(await apiRequest<JsonValue>(`/projects/${projectId}/timeline-time/${result.job_id}`, { signal: request.signal }))
      }
      if (result.status !== 'success') throw new Error(result.error || `Time edit ${result.status}`)
      if (!result.source_path || !result.duration) throw new Error('Time edit completed without media')
      const current = getClip(clip.id)
      if (!current || current.sourcePath !== clip.sourcePath || current.trimStart !== clip.trimStart
        || current.trimEnd !== clip.trimEnd || current.originalDuration !== clip.originalDuration
        || current.speed !== clip.speed || current.startOffset !== clip.startOffset) {
        log({ event: 'time-edit-result-rejected', project_id: projectId, clip_id: clip.id, job_id: result.job_id,
          mode, reason: 'clip-changed', original_speed: clip.speed, current_speed: current?.speed,
          original_offset: clip.startOffset, current_offset: current?.startOffset })
        throw new Error('The clip changed while processing. Select it and try again.')
      }
      onApply(clip.id, result.source_path, result.duration, mode, mode === 'speed_curve' && points ? {
        source_path: body.source_path, source_start: body.start_time, source_end: body.end_time,
        points, render_duration: result.duration,
      } : undefined)
      log({ event: 'time-edit-result-dispatched', project_id: projectId, clip_id: clip.id, job_id: result.job_id,
        mode, duration: result.duration, source_path: result.source_path })
    } catch (failure) {
      if (!request.signal.aborted) setError(failure instanceof Error ? failure.message : String(failure))
    } finally {
      if (!request.signal.aborted) setBusy(false)
      controller.current = null
    }
  }, [projectId, getClip, onApply])
  return { run, busy, error }
}
